import { useEffect, useState, useRef } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Portal,
  Typography,
} from '@mui/material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PersonIcon from '@mui/icons-material/Person';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import { motion } from 'framer-motion';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { startOutboundCall, hangupCall } from '../../services/voice/voiceCallService';

function formatTimer(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${String(h).padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * ContactListCallDialog
 *
 * Ringing / ended states  → centered MUI Dialog (blocks UI, no dismiss until done)
 * Connected state         → compact draggable floating widget (user can move it aside)
 *
 * Props:
 *   open         {boolean}  — controls visibility
 *   onClose      {Function} — called when user dismisses after call ends
 *   contactList  {object}   — full contact list ({ id, name, members, … })
 *   tenantId     {string}
 *   sessionId    {string}   — unique session ID generated by caller
 */
export default function ContactListCallDialog({ open, onClose, contactList, tenantId, sessionId }) {
  const [callState,    setCallState]    = useState('ringing');
  const [errorMsg,     setErrorMsg]     = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const userCancelledRef  = useRef(false);
  const callStartedRef    = useRef(false);
  const callWasAnsweredRef = useRef(false);
  const timerRef          = useRef(null);

  const listName      = contactList?.name || 'Contact List';
  const activeMembers = (contactList?.members || []).filter((m) => m.active !== false);

  useEffect(() => {
    if (!open) return;
    setCallState('ringing');
    setErrorMsg('');
    setCallDuration(0);
    userCancelledRef.current   = false;
    callStartedRef.current     = false;
    callWasAnsweredRef.current = false;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, [open, sessionId]);

  useEffect(() => {
    if (!open || !sessionId || !tenantId) return;
    const db         = getFirestore();
    const sessionRef = doc(db, 'tenants', tenantId, 'contactListCallSessions', sessionId);
    const unsub      = onSnapshot(sessionRef, (snap) => {
      if (!snap.exists()) return;
      const { status } = snap.data() || {};
      if (status === 'active') { callWasAnsweredRef.current = true; setCallState('connected'); }
    });
    return () => unsub();
  }, [open, sessionId, tenantId]);

  useEffect(() => {
    if (callState === 'connected') {
      setCallDuration(0);
      timerRef.current = setInterval(() => setCallDuration((s) => s + 1), 1000);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [callState]);

  useEffect(() => {
    if (!open || !contactList?.id || !tenantId || !sessionId) return;
    if (callStartedRef.current) return;
    callStartedRef.current = true;
    startOutboundCall({ action: 'contact_list_call', contactListId: contactList.id, tenantId, sessionId })
      .catch((e) => { setErrorMsg(e?.message || 'Failed to start call.'); setCallState('failed'); });
  }, [open, contactList?.id, tenantId, sessionId]);

  useEffect(() => {
    if (!open) return;
    const onActive = () => setCallState('connected');
    const onEnded  = () => {
      if (userCancelledRef.current) { onClose(); return; }
      setCallState((prev) => {
        if (prev === 'connected' || callWasAnsweredRef.current) return 'ended';
        if (prev === 'ringing') return 'no_answer';
        return prev;
      });
    };
    const onError = (e) => { setErrorMsg(e?.detail?.error || 'Call error.'); setCallState('failed'); };
    window.addEventListener('voice-call-active',    onActive);
    window.addEventListener('voice-call-ended',     onEnded);
    window.addEventListener('voice-outbound-error', onError);
    return () => {
      window.removeEventListener('voice-call-active',    onActive);
      window.removeEventListener('voice-call-ended',     onEnded);
      window.removeEventListener('voice-outbound-error', onError);
    };
  }, [open, onClose]);

  const handleCancel = () => { userCancelledRef.current = true; hangupCall(); };
  const handleHangUp = () => { userCancelledRef.current = true; hangupCall(); };
  const handleClose  = () => { callStartedRef.current = false; onClose(); };

  const isRinging   = callState === 'ringing';
  const isConnected = callState === 'connected';
  const isEnded     = callState === 'ended';
  const isNoAnswer  = callState === 'no_answer';
  const isFailed    = callState === 'failed';
  const isDone      = isEnded || isNoAnswer || isFailed;

  return (
    <>
      {/* ── Ringing / ended states: centered blocking dialog ── */}
      <Dialog open={open && !isConnected} onClose={isDone ? handleClose : undefined} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 0.5 }}>
          {isRinging  && listName}
          {isNoAnswer && 'No Answer'}
          {isEnded    && 'Call Ended'}
          {isFailed   && 'Call Failed'}
        </DialogTitle>

        <DialogContent>
          {isRinging && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <CircularProgress size={20} thickness={4} />
                <Typography variant="body2" color="text.secondary">Ringing members…</Typography>
              </Box>
              {activeMembers.length > 0 && (
                <List disablePadding dense>
                  {activeMembers
                    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
                    .map((m) => {
                      const name     = m.displayName || m.userId;
                      const initials = (name || '?').split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
                      return (
                        <ListItem key={m.id} disableGutters sx={{ py: 0.25 }}>
                          <ListItemAvatar sx={{ minWidth: 36 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 11 }}>
                              {initials || <PersonIcon fontSize="small" />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={name}
                            secondary={m.email || null}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      );
                    })}
                </List>
              )}
            </>
          )}

          {isNoAnswer && <Typography variant="body2" color="text.secondary">No members answered.</Typography>}
          {isEnded    && <Typography variant="body2" color="text.secondary">The call has ended.</Typography>}
          {isFailed   && <Typography variant="body2" color="error">{errorMsg || 'Unable to place call.'}</Typography>}
        </DialogContent>

        {!isDone && <Divider />}

        <DialogActions>
          {isRinging && (
            <Button variant="contained" color="error" startIcon={<CallEndIcon />} onClick={handleCancel}>
              Cancel
            </Button>
          )}
          {isDone && <Button onClick={handleClose}>Close</Button>}
        </DialogActions>
      </Dialog>

      {/* ── Connected state: draggable floating widget ── */}
      {open && isConnected && (
        <Portal>
          <motion.div
            drag
            dragMomentum={false}
            initial={{ opacity: 0, scale: 0.85, y: 20 }}
            animate={{ opacity: 1, scale: 1,    y: 0  }}
            style={{
              position: 'fixed',
              bottom:   90,
              right:    24,
              zIndex:   1500,
              userSelect: 'none',
            }}
          >
            <Paper
              elevation={12}
              sx={{ width: 280, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'success.main' }}
            >
              {/* Drag handle */}
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.5, py: 1,
                  bgcolor: 'success.dark',
                  color: 'white',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                }}
              >
                <DragIndicatorIcon fontSize="small" sx={{ opacity: 0.8 }} />
                <PhoneInTalkIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1 }}>
                  {listName}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>Connected</Typography>
              </Box>

              {/* Timer */}
              <Box sx={{ px: 2, pt: 2, pb: 1, textAlign: 'center' }}>
                <Typography
                  variant="h4"
                  sx={{ fontFamily: 'monospace', color: 'success.main', letterSpacing: 3, fontWeight: 700 }}
                >
                  {formatTimer(callDuration)}
                </Typography>
              </Box>

              <Divider />

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<CallEndIcon />}
                  onClick={handleHangUp}
                >
                  Hang Up
                </Button>
              </Box>
            </Paper>
          </motion.div>
        </Portal>
      )}
    </>
  );
}
